// Read-only doc viewer overlay (D-012, sprint md-doc-viewer; widened in
// D-019 to image / PDF / text formats).
//
// Mounted once at the top of the authenticated shell. Open/close is driven
// by the `?doc=...&agent=...` query string (see useDocViewer). On open it
// fetches /api/docs and renders the body using the format suggested by
// the response's `Content-Type`:
//
//   - text/markdown → MarkdownRenderer (existing path).
//   - text/plain or application/json → mono `<pre>` block.
//   - image/* → centred `<img>` (object URL from response Blob).
//   - application/pdf → `<embed type="application/pdf">` (with iOS
//     fallback because mobile Safari ignores `<embed>` for PDFs).
//
// Internal `[X](other.md)` links resolve against the current doc's
// directory and update the query string in-place (the override is local
// to this component — chat behaviour is unchanged).
//
// The sheet header always renders a Download button that hits the same
// `/api/docs` URL with `&download=1` so the response carries
// `Content-Disposition: attachment` and the browser saves the file.

import { useEffect, useMemo, useState } from 'react';
import type { Components } from 'react-markdown';
// DS.7 D17/D8b — `@ds/overlays/SideSheet` (`variant="modal"`,
// `width="min(720px, 90vw)"`) through `ds/DsSideSheet`, which adds the focus
// trap the piece does not have (`SideSheet.tsx:38-40` is its whole keyboard
// surface, while today's Radix `SheetContent` traps Tab) and registers the
// sheet on the campaign's layer stack. `hideChrome` suppresses the piece's own
// header row: this sheet renders its own — the path chip, the download link
// and the close button, three anchors AC-12 pins — and two close affordances
// stacked is not a design, it is an accident.
import { DsSideSheet } from './ds/DsSideSheet';
import { MarkdownRenderer } from './MarkdownRenderer';
import { Icon } from './atoms/Icon';
import { Spinner } from './atoms/Spinner';
import { getActiveBaseUrl } from '../stores/servers.store';
import { ACCESS_GATE_FETCH_INIT, isAccessGateRedirect } from '../lib/api';
import { useDocViewer } from '../hooks/useDocViewer';
import { useTranslation } from '../i18n';

type DocPayload =
  | { kind: 'text'; mime: string; body: string }
  | { kind: 'image'; mime: string; objectUrl: string }
  | { kind: 'pdf'; objectUrl: string };

type DocState =
  | { kind: 'loading' }
  | { kind: 'ready'; payload: DocPayload }
  | { kind: 'error'; code: string; message: string };

function resolveRelativePath(baseDocPath: string, href: string): string {
  if (href.startsWith('/')) {
    // Absolute path inside the project root — strip the leading slash so
    // the endpoint resolves it relative to workdir.
    return href.replace(/^\/+/, '');
  }
  const lastSlash = baseDocPath.lastIndexOf('/');
  const baseDir = lastSlash >= 0 ? baseDocPath.slice(0, lastSlash) : '';
  const combined = baseDir ? `${baseDir}/${href}` : href;
  // Normalise `..` and `.` segments.
  const segments = combined.split('/');
  const out: string[] = [];
  for (const seg of segments) {
    if (seg === '' || seg === '.') continue;
    if (seg === '..') {
      out.pop();
      continue;
    }
    out.push(seg);
  }
  return out.join('/');
}

function isExternalHref(href: string): boolean {
  return /^[a-z][a-z0-9+.\-]*:/i.test(href) || href.startsWith('//');
}

function isMdHref(href: string): boolean {
  // Strip any trailing query / fragment before the extension check.
  const cleaned = href.split(/[?#]/)[0] ?? href;
  return cleaned.toLowerCase().endsWith('.md');
}

// Heuristic — iOS Safari (incl. iPad on iOS 13+) ignores `<embed>` for
// PDFs and renders a blank box. Detect via UA so we can render a
// fallback message + Download CTA. Pre-mortem failure mode #6.
function isIosLike(userAgent: string): boolean {
  if (/iPad|iPhone|iPod/.test(userAgent)) return true;
  // iPadOS reports "MacIntel" as platform but the UA contains
  // "Mac OS X" with a Touch presence — this is the closest UA-only
  // heuristic short of a feature probe.
  if (/Macintosh/.test(userAgent) && /Mobile/.test(userAgent)) return true;
  return false;
}

export function DocViewerSheet() {
  const { current, navigateTo, close } = useDocViewer();
  const { t } = useTranslation();
  const [state, setState] = useState<DocState>({ kind: 'loading' });

  // Stable URL for both the in-overlay fetch AND the Download anchor.
  const docUrl = useMemo(() => {
    if (!current) return null;
    const base = getActiveBaseUrl();
    return `${base}/api/docs?agentId=${encodeURIComponent(
      current.agentId,
    )}&path=${encodeURIComponent(current.path)}`;
  }, [current]);

  useEffect(() => {
    if (!current || !docUrl) return;
    let cancelled = false;
    let createdObjectUrl: string | null = null;
    setState({ kind: 'loading' });

    const controller = new AbortController();

    fetch(docUrl, {
      method: 'GET',
      credentials: 'include',
      headers: { 'X-Dashboard-Request': '1' },
      signal: controller.signal,
      // D1 (sprint access-session-recovery, challenger R1 change 1) — this
      // viewer does its own raw `fetch` + hand-rolled error parse, same
      // class of gap uploadAttachment had. Shares the ONE exported
      // predicate from `lib/api.ts` instead of a fourth copy.
      ...ACCESS_GATE_FETCH_INIT,
    })
      .then(async (res) => {
        if (cancelled) return;
        // Classification runs BEFORE the hand-rolled parse below (R10): a
        // body read on an opaqueredirect response degrades to a bare
        // `HTTP_ERROR`/`HTTP 0` instead of the real cause.
        if (isAccessGateRedirect(res)) {
          setState({ kind: 'error', code: 'ACCESS_GATE', message: '' });
          return;
        }
        if (!res.ok) {
          let code = 'HTTP_ERROR';
          let message = `HTTP ${res.status}`;
          try {
            const json = (await res.json()) as {
              error?: { code?: string; message?: string };
            };
            code = json.error?.code ?? code;
            message = json.error?.message ?? message;
          } catch {
            // not JSON, keep defaults
          }
          if (!cancelled) setState({ kind: 'error', code, message });
          return;
        }
        const mime = (res.headers.get('content-type') ?? '').toLowerCase();
        if (mime.startsWith('image/')) {
          const blob = await res.blob();
          if (cancelled) return;
          createdObjectUrl = URL.createObjectURL(blob);
          setState({
            kind: 'ready',
            payload: { kind: 'image', mime, objectUrl: createdObjectUrl },
          });
        } else if (mime.startsWith('application/pdf')) {
          const blob = await res.blob();
          if (cancelled) return;
          createdObjectUrl = URL.createObjectURL(blob);
          setState({
            kind: 'ready',
            payload: { kind: 'pdf', objectUrl: createdObjectUrl },
          });
        } else {
          // text/markdown, text/plain, application/json — every other
          // allowed MIME on the path-safety table is text and decodes
          // cleanly via response.text().
          const body = await res.text();
          if (cancelled) return;
          setState({
            kind: 'ready',
            payload: { kind: 'text', mime, body },
          });
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if ((err as { name?: string }).name === 'AbortError') return;
        setState({
          kind: 'error',
          code: 'NETWORK',
          message: err instanceof Error ? err.message : 'Network error',
        });
      });

    return () => {
      cancelled = true;
      controller.abort();
      if (createdObjectUrl) URL.revokeObjectURL(createdObjectUrl);
    };
  }, [current, docUrl]);

  // Local `a` override so internal `.md` links navigate inside the sheet.
  const linkOverrides: Partial<Components> = {
    a: ({ children, href, ...rest }) => {
      const safeHref = href ?? '';
      if (!isExternalHref(safeHref) && isMdHref(safeHref)) {
        return (
          <a
            href={`?doc=${encodeURIComponent(
              resolveRelativePath(current?.path ?? '', safeHref),
            )}`}
            onClick={(e) => {
              e.preventDefault();
              if (current) {
                navigateTo(resolveRelativePath(current.path, safeHref));
              }
            }}
            className="text-action hover:text-action/80 underline underline-offset-2"
            data-testid="doc-internal-link"
            {...rest}
          >
            {children}
          </a>
        );
      }
      return (
        <a
          href={safeHref}
          target="_blank"
          rel="noopener noreferrer"
          className="text-action hover:text-action/80 underline underline-offset-2"
          {...rest}
        >
          {children}
        </a>
      );
    },
  };

  const downloadUrl = docUrl ? `${docUrl}&download=1` : null;
  const downloadFilename = current?.path.split('/').pop() ?? 'download';

  return (
    <DsSideSheet
      open={current !== null}
      onClose={close}
      variant="modal"
      width="min(720px, 90vw)"
      hideChrome
      data-testid="doc-viewer-sheet"
    >
        <header className="flex items-center justify-between gap-2 border-b border-border px-4 py-3 bg-card">
          <div className="flex items-center gap-2 min-w-0">
            <Icon name="FileText" size={14} aria-hidden />
            <span
              className="text-body-chat font-medium truncate"
              title={current?.path ?? ''}
              data-testid="doc-viewer-path"
            >
              {current?.path ?? ''}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {downloadUrl && (
              <a
                href={downloadUrl}
                download={downloadFilename}
                className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors duration-100 inline-flex items-center"
                aria-label={t('doc.download')}
                data-testid="doc-viewer-download"
              >
                <Icon name="Download" size={16} aria-hidden />
              </a>
            )}
            <button
              type="button"
              onClick={close}
              className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors duration-100"
              aria-label={t('doc.close')}
              data-testid="doc-viewer-close"
            >
              <Icon name="X" size={16} aria-hidden />
            </button>
          </div>
        </header>

        <div
          className="flex-1 min-h-0 overflow-y-auto px-5 py-4"
          data-testid="doc-viewer-body"
        >
          {state.kind === 'loading' && (
            <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
              <Spinner size="sm" />
              <span className="text-body-chat">{t('doc.loading')}</span>
            </div>
          )}
          {state.kind === 'error' && (
            <DocError state={state} path={current?.path ?? ''} />
          )}
          {state.kind === 'ready' && (
            <DocPreview
              payload={state.payload}
              linkOverrides={linkOverrides}
              filename={downloadFilename}
            />
          )}
        </div>
    </DsSideSheet>
  );
}

function DocPreview({
  payload,
  linkOverrides,
  filename,
}: {
  payload: DocPayload;
  linkOverrides: Partial<Components>;
  filename: string;
}) {
  const { t } = useTranslation();
  if (payload.kind === 'text') {
    if (payload.mime.startsWith('text/markdown')) {
      return (
        <MarkdownRenderer text={payload.body} extraComponents={linkOverrides} />
      );
    }
    return (
      <pre
        className="text-mono text-foreground whitespace-pre-wrap break-words"
        data-testid="doc-viewer-text"
        data-mime={payload.mime}
      >
        {payload.body}
      </pre>
    );
  }
  if (payload.kind === 'image') {
    return (
      <img
        src={payload.objectUrl}
        className="mx-auto max-h-[80vh] object-contain"
        alt={filename}
        data-testid="doc-viewer-image"
      />
    );
  }
  // PDF — embed inline on desktop, fallback message on iOS where
  // `<embed type="application/pdf">` renders blank (pre-mortem FM6).
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (isIosLike(ua)) {
    return (
      <div
        className="text-body-chat text-muted-foreground py-8 text-center"
        data-testid="doc-viewer-pdf-ios-fallback"
      >
        {t('doc.pdfIosFallback')}
      </div>
    );
  }
  return (
    <embed
      src={payload.objectUrl}
      type="application/pdf"
      className="w-full h-[80vh]"
      data-testid="doc-viewer-pdf"
    />
  );
}

function DocError({
  state,
  path,
}: {
  state: { kind: 'error'; code: string; message: string };
  path: string;
}) {
  const { t } = useTranslation();
  let message: string;
  switch (state.code) {
    case 'NOT_FOUND':
      message = t('doc.notFound', { path });
      break;
    case 'ENTITY_TOO_LARGE':
      message = t('doc.tooLarge');
      break;
    case 'BAD_PATH':
      message = t('doc.forbidden');
      break;
    case 'UNAUTHENTICATED':
      message = t('doc.sessionExpired');
      break;
    case 'ACCESS_GATE':
      message = t('accessGate.docViewerBlocked');
      break;
    case 'NO_WORKDIR':
      message = t('doc.noWorkdir');
      break;
    case 'NETWORK':
    default:
      message = t('doc.networkError');
      break;
  }
  return (
    <div
      className="text-body-chat text-muted-foreground py-8 text-center"
      data-testid="doc-viewer-error"
      data-error-code={state.code}
    >
      {message}
    </div>
  );
}
